import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { Alert, Button, Checkbox, Col, Form, Input, notification, Row, Select, Typography } from 'antd'

import { GoogleOutlined, LinkedinOutlined } from '@ant-design/icons'
import { getCountryCallingCode, isValidPhoneNumber, parsePhoneNumber } from 'react-phone-number-input'
import QueryString from 'query-string'
import cryptoRandomString from 'crypto-random-string'
import SpinLoader from '../../../helpers/SpinLoader'
import { useAuthContext } from '../../../../context/auth'
import { IdentityProviders, IntegrationTypes, TRegisterRequest } from '../../../../context/types'
import { useGoogle } from '../../../helpers/google'
import { linkedinClientId, linkedinRegisterRedirectUri } from '../../../../config'

import Countries from '../../../../utils/countries'
import { customLocalStorage } from '../../../../context/customStorage'
import { useIntegrationContext } from '../../../../context/integration'
import './index.less'
import { DEFAULT_COUNTRY } from '../../../../constants'

const { Text, Title, Paragraph, Link: AntLink } = Typography
const { Option } = Select
const { TextArea } = Input

type TRegisterProps = {
	onGoToLogin?: () => void
	skipRedirect?: boolean
	isPromo?: boolean
	redirectUrl?: string
	redirectOnRegister?: string
	allowedIDPs?: IdentityProviders[]
	allowedDomains?: string[]
}

function RegisterForm({
	onGoToLogin,
	skipRedirect,
	redirectUrl,
	isPromo,
	redirectOnRegister,
	allowedIDPs = [],
	allowedDomains = [],
}: TRegisterProps) {
	const { error, code, error_description } = QueryString.parse(window.location.search)
	const [tosAccept, setTosAccept] = useState<boolean>(false)
	const [showContestRules, setShowContestRules] = useState<boolean>(false)
	const [contestRulesAccept, setContestRulesAccept] = useState<boolean>(false)
	const [formErrors, setFormErrors] = useState([])
	const [customCheckboxesValues, setCustomCheckboxesValues] = useState<boolean[]>([])

	const [form] = Form.useForm()

	const { auth, actions } = useAuthContext()
	const { integration } = useIntegrationContext()

	const history = useHistory()

	const { isInviteExpert, loading } = integration

	const googleAuth = useGoogle(
		(tokenId: string) => actions.googleRegister(tokenId, skipRedirect, redirectOnRegister || redirectUrl),
		actions.setError
	)

	const checkAcceptedCheckbox = useMemo(() => {
		if (!integration.content?.register_checkboxes?.length) {
			return true
		}

		return integration.content?.register_checkboxes?.reduce(
			(res, checkbox, index) => res && (!checkbox.required || customCheckboxesValues[index]),
			true
		)
	}, [integration.content?.register_checkboxes, customCheckboxesValues])

	const allAccepted = useMemo(() => {
		return tosAccept && (contestRulesAccept || !showContestRules) && checkAcceptedCheckbox
	}, [tosAccept, showContestRules, contestRulesAccept])

	const handleGoogleAuth = useCallback(() => {
		if (allAccepted) {
			googleAuth()
		} else {
			const customCheckboxesNames = integration.content?.register_checkboxes?.map(({ name }) => name)
			form.validateFields(['tos_and_privacy', ...(customCheckboxesNames || [])])
			notification.error({
				message: 'You have to accept the Terms of Service and Privacy Policy!',
			})
		}
	}, [tosAccept, form, allAccepted])

	const newState = cryptoRandomString({ length: 10, type: 'base64' })
	const linkedinUrl = `https://www.linkedin.com/oauth/v2/authorization?response_type=code&client_id=${linkedinClientId}&redirect_uri=${linkedinRegisterRedirectUri}&scope=r_emailaddress,r_liteprofile&state=${newState}`
	const handleLinkedinAuth = useCallback(() => {
		if (allAccepted) {
			if (redirectOnRegister) {
				customLocalStorage.setItem('redirectUrl', redirectOnRegister)
			}
			window.location.href = linkedinUrl
		} else {
			form.validateFields(['tos_and_privacy'])
			notification.error({
				message: 'You have to accept the Terms of Service and Privacy Policy!',
			})
		}
	}, [form, linkedinUrl, tosAccept, allAccepted])

	useEffect(() => {
		actions.setError()
		if (code) {
			actions.linkedinRegister(code as string, skipRedirect, redirectOnRegister || redirectUrl)
			history.replace({ search: '' })
		}
		if (redirectUrl) {
			customLocalStorage.setItem('redirectUrl', redirectUrl)
		}
	}, [])

	useEffect(() => {
		if (integration.content?.register_checkboxes?.length) {
			setCustomCheckboxesValues(new Array(integration.content.register_checkboxes?.length).fill(false))
		}
	}, [integration.content?.register_checkboxes])

	useEffect(() => {
		if (!isInviteExpert && integration.content?.register_member && integration.name === 'Staples Connect') {
			setShowContestRules(true)
		}
	}, [integration.name, integration.content, isInviteExpert])

	const reset = useCallback(() => actions.setError(), [actions])

	const onFinish = (values: TRegisterRequest) =>
		actions.register(values, skipRedirect, redirectOnRegister || redirectUrl)

	const onFieldsChange = () => {
		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	if (error) {
		actions.setError((error_description as string) || 'Register failed! Please try again.')
	}

	const goToLogin = onGoToLogin ? onGoToLogin : () => history.push('/login')

	const triggerValidatePhoneField = useCallback(
		(option) => {
			const values = form.getFieldsValue()
			form.setFieldsValue({
				...values,
				phone_country_id: getCountryCallingCode(option),
			})
			form.validateFields(['phone'])
		},
		[form]
	)

	const prefixSelector = (
		<>
			<Form.Item name="phone_country_code" noStyle>
				<Select
					filterOption={(input, option) => {
						return (
							option['data-code'].toLowerCase().indexOf(input.toLowerCase().replace('+', '')) >= 0 ||
							option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
						)
					}}
					showSearch
					style={{ width: 105 }}
					onChange={triggerValidatePhoneField}
				>
					{Countries.options.map(({ code, flag, country }, index) => (
						<Option key={index} value={`${country}`} data-code={code}>
							<span className="country-flag">{flag}</span>
							<span className="country-code">+{code}</span>
						</Option>
					))}
				</Select>
			</Form.Item>
			<Form.Item name="phone_country_id" style={{ display: 'none' }}>
				<Input autoComplete="off" disabled={!!auth.loading} />
			</Form.Item>
		</>
	)

	const registerErrorText = useMemo(() => <span dangerouslySetInnerHTML={{ __html: auth.error }} />, [auth.error])

	const expertFormTitle = useMemo(() => {
		return integration.content?.register_expert_form_title || 'Are you IN? Join today'
	}, [integration.content?.register_expert_form_title])
	const allIDPsAvailable = allowedIDPs.length === 0
	const displayRegisterWith =
		allIDPsAvailable ||
		allowedIDPs.includes(IdentityProviders.GOOGLE) ||
		allowedIDPs.includes(IdentityProviders.LINKED_IN)
	const shouldDisplayIDP = (idp: IdentityProviders) => allIDPsAvailable || allowedIDPs.includes(idp)
	const isPhoneRequired = useMemo(() => {
		return isInviteExpert
			? integration.phone_rules?.phone_required_to_register_expert ?? true
			: integration.phone_rules?.phone_required_to_register_member ?? false
	}, [isInviteExpert, integration.phone_rules])

	return (
		<SpinLoader loading={!!auth.loading || loading}>
			<Form
				name="RegisterForm"
				form={form}
				onFinish={onFinish}
				scrollToFirstError
				layout="vertical"
				className="register-form"
				onValuesChange={reset}
				onFieldsChange={onFieldsChange}
				initialValues={{
					phone_country_id: '1',
					phone_country_code: DEFAULT_COUNTRY.iso,
				}}
			>
				{!loading ? (
					<>
						<div className="title-container">
							{isPromo && (
								<Title level={2} style={{ marginRight: 15 }} className="desktop">
									{isInviteExpert ? expertFormTitle : loading ? '\u00A0' : 'Join FREE today!'}
								</Title>
							)}
							<Title level={2} className={isPromo ? 'mobile' : ''}>
								{loading ? '\u00A0' : 'Sign Up'}
							</Title>
							{(!integration.type || integration.type === IntegrationTypes.top_level) && (
								<div className="social-register">
									{displayRegisterWith && <span>or register with</span>}

									{shouldDisplayIDP(IdentityProviders.GOOGLE) && (
										<Button
											style={{ borderRadius: '50%' }}
											type="primary"
											shape="circle"
											icon={<GoogleOutlined />}
											onClick={handleGoogleAuth}
											loading={!!auth.loading}
										/>
									)}

									{shouldDisplayIDP(IdentityProviders.LINKED_IN) && (
										<Button
											style={{ borderRadius: '50%' }}
											type="primary"
											shape="circle"
											icon={<LinkedinOutlined />}
											onClick={handleLinkedinAuth}
											loading={!!auth.loading}
										/>
									)}
								</div>
							)}
						</div>
						{shouldDisplayIDP(IdentityProviders.EMAIL) && (
							<Row gutter={[50, 0]} align="top">
								<Col xs={24} lg={12}>
									<Form.Item
										label="First Name"
										name="first_name"
										rules={[{ required: true, message: 'Please input your First Name!' }]}
									>
										<Input autoComplete="off" />
									</Form.Item>
								</Col>
								<Col xs={24} lg={12}>
									<Form.Item
										label="Last Name"
										name="last_name"
										rules={[{ required: true, message: 'Please input your Last Name!' }]}
									>
										<Input autoComplete="off" />
									</Form.Item>
								</Col>

								<Col xs={24} lg={12}>
									<Form.Item
										name="phone"
										label="Mobile Number"
										rules={[
											{
												required: isPhoneRequired,
											},
											{
												validator: (_rule, value) => {
													const phoneCountryId = form.getFieldValue('phone_country_id')
													const phoneValidation = parsePhoneNumber(`+${phoneCountryId}${value}`)

													if (
														!value ||
														phoneValidation?.nationalNumber !== value ||
														isValidPhoneNumber(`+${phoneCountryId}${value}`)
													) {
														return Promise.resolve()
													}

													return Promise.reject()
												},
												message: 'The input is not valid mobile number!',
											},
										]}
									>
										<Input autoComplete="off" addonBefore={prefixSelector} style={{ width: '100%' }} type="number" />
									</Form.Item>
								</Col>
								<Col xs={24} lg={12}>
									<Form.Item
										name="email"
										label="Email"
										rules={[
											{
												type: 'email',
												message: 'The input is not valid E-mail!',
											},
											{
												required: true,
												message: 'Please input your E-mail!',
											},
											() => ({
												validator(_, value) {
													if (!value || allowedDomains.length === 0) return Promise.resolve()

													return allowedDomains.includes(value.split('@')?.pop()?.toLowerCase())
														? Promise.resolve()
														: Promise.reject(
																`Please login using your channel corporate address. If you are not sure what to use, please reach out to info@getwiseconnect.com`
														  )
												},
											}),
										]}
									>
										<Input autoComplete="off" />
									</Form.Item>
								</Col>
								<Col xs={{ span: 24 }} lg={{ span: 12 }}>
									<Form.Item
										name="password"
										label="Password"
										rules={[
											{
												required: true,
												message: 'Please input your password!',
											},
										]}
										hasFeedback
									>
										<Input.Password />
									</Form.Item>
								</Col>
								<Col xs={24} lg={12}>
									<Form.Item
										name="confirmPassword"
										label="Confirm Password"
										dependencies={['password']}
										hasFeedback
										rules={[
											{
												required: true,
												message: 'Please confirm your password!',
											},
											({ getFieldValue }) => ({
												validator(_, value) {
													if (!value || getFieldValue('password') === value) {
														return Promise.resolve()
													}
													return Promise.reject(new Error('The two passwords that you entered do not match!'))
												},
											}),
										]}
									>
										<Input.Password />
									</Form.Item>
								</Col>
								{isInviteExpert && integration.onboarding?.referred_by !== false ? (
									<Col xs={24}>
										<Form.Item
											label="Referred by"
											name="referred_by"
											rules={[{ required: true, message: 'Please input referred by field.' }]}
										>
											<TextArea
												rows={2}
												placeholder="Please enter the name of the person who referred you or enter N/A"
											/>
										</Form.Item>
									</Col>
								) : null}
							</Row>
						)}
						<Form.Item
							name="tos_and_privacy"
							valuePropName="checked"
							rules={[
								{
									validator: (_, value) => {
										setTosAccept(!!value)
										if (value) {
											return Promise.resolve()
										}
										return Promise.reject(new Error('Should accept agreement'))
									},
								},
							]}
						>
							<Checkbox>
								<Text strong>
									I accept the{' '}
									<AntLink href="https://www.getwiseconnect.com/tos" target="_blank">
										Terms of Service
									</AntLink>
									{' and '}
									<AntLink href="http://www.getwiseconnect.com/privacy" target="_blank">
										Privacy Policy
									</AntLink>
								</Text>
							</Checkbox>
						</Form.Item>
						{showContestRules && (
							<Form.Item
								name="contest_rules"
								valuePropName="checked"
								rules={[
									{
										validator: (_, value) => {
											setContestRulesAccept(!!value)
											if (value) {
												return Promise.resolve()
											}
											return Promise.reject(new Error('Should accept contest rules'))
										},
									},
								]}
							>
								<Checkbox>
									<Text strong>
										I have read and agree to the{' '}
										<AntLink href="https://wiseher.com/big-small-business-boost-contest-rules/" target="_blank">
											contest rules
										</AntLink>
									</Text>
								</Checkbox>
							</Form.Item>
						)}
						{integration.content?.register_checkboxes?.map((checkbox, index) => (
							<Form.Item
								name={checkbox.name}
								key={checkbox.name}
								valuePropName="checked"
								required={!!checkbox.required}
								rules={[
									{
										validator: (_, value) => {
											const newValues = [...customCheckboxesValues]
											newValues[index] = !!value
											setCustomCheckboxesValues(newValues)
											if (!checkbox.required || !!value) {
												return Promise.resolve()
											}
											return Promise.reject(new Error(checkbox.error))
										},
									},
								]}
							>
								<Checkbox>
									<Text strong>
										<span dangerouslySetInnerHTML={{ __html: checkbox.label }} />
									</Text>
								</Checkbox>
							</Form.Item>
						))}
						{auth.error && (
							<Alert
								showIcon
								message="Error"
								description={registerErrorText}
								type="error"
								style={{ marginBottom: 20 }}
							/>
						)}
						{formErrors.length > 0 && (
							<Alert
								showIcon
								type="warning"
								message="Warning"
								style={{ marginBottom: 20 }}
								description={formErrors.map((v) => (
									<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
										{v.errors[0]}
									</Paragraph>
								))}
							/>
						)}

						<Row align="middle">
							<Col flex="none">
								<Form.Item>
									<Button
										shape="round"
										type="primary"
										htmlType="submit"
										loading={!!auth.loading}
										disabled={!shouldDisplayIDP(IdentityProviders.EMAIL)}
									>
										Register
									</Button>
								</Form.Item>
							</Col>
							<Col flex="none">
								<Form.Item>
									<Button shape="round" type="link" onClick={goToLogin} loading={!!auth.loading}>
										<b>Log In</b>
									</Button>
								</Form.Item>
							</Col>
						</Row>
					</>
				) : null}
			</Form>
		</SpinLoader>
	)
}

export default RegisterForm
